import cls from "classnames";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentType,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { Link, LinkProps } from "react-router-dom";

import { omit } from "utils/object";
import type { WithDiscriminatedProps } from "utils/types";

import Spinner from "components/ui/Spinner";

import styles from "./index.module.scss";

type CommonProps = {
  children?: ReactNode;
  variant?: "primary" | "secondary" | "plain" | "danger" | "danger-outline";
  size?: "extra-small" | "small" | "medium" | "large" | "extra-large";
  loading?: boolean;
  selected?: boolean;
  disabled?: boolean;
  Icon?: ComponentType<{ className?: string; htmlColor?: string }>;
  iconColor?: string;
  className?: string;
  // e2e markers (HTMLAttributes has no data-* index signature for custom components).
  "data-cy"?: string;
  "data-testid"?: string;
};

type ButtonAttributes = ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorAttributes = AnchorHTMLAttributes<HTMLAnchorElement>;

type Props =
  | WithDiscriminatedProps<CommonProps, { as?: "button" }, ButtonAttributes>
  | WithDiscriminatedProps<CommonProps, { as: "a"; href: string }, AnchorAttributes>
  | WithDiscriminatedProps<CommonProps, { as: "link" }, LinkProps>;

// Peeled per branch rather than destructured in the header: a union's rest
// element does not narrow with the discriminant.
const KIT_PROPS = [
  "as",
  "children",
  "variant",
  "size",
  "loading",
  "selected",
  "disabled",
  "Icon",
  "iconColor",
  "className",
] as const;

/**
 * The app's button, rendering as a `<button>`, an `<a>` or a router `Link`.
 *
 * The `<button>` form ignores repeat clicks for half a second after the first,
 * so a double click cannot run a mutating action twice. The two link forms are
 * not guarded.
 */
export default function Button(props: Props) {
  // `disabled` does nothing on an anchor, so the two link forms have to be made
  // inert by hand: no destination, out of the tab order, and announced as
  // disabled rather than simply gone.
  const inert = props.disabled || props.loading;
  const guardClick = useClickGuard();
  const className = buttonClassName(props);
  const content = (
    <>
      {props.loading && (
        <Spinner placement="inline" size="small" color="var(--c-slate-300)" />
      )}
      {props.Icon && (
        <props.Icon
          className={cls(styles.icon, !props.iconColor && styles.iconInk)}
          htmlColor={props.iconColor}
        />
      )}
      {props.children}
    </>
  );

  if (props.as === "a") {
    const { href, ...rest } = omit(props, KIT_PROPS);
    return (
      <a
        {...rest}
        className={className}
        href={inert ? undefined : href}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : rest.tabIndex}
        onClick={inert ? undefined : rest.onClick}
      >
        {content}
      </a>
    );
  }

  if (props.as === "link") {
    const rest = omit(props, KIT_PROPS);
    return (
      <Link
        {...rest}
        className={className}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : rest.tabIndex}
        onClick={inert ? cancelNavigation : rest.onClick}
      >
        {content}
      </Link>
    );
  }

  const { onClick, ...rest } = omit(props, KIT_PROPS);
  return (
    // Before the spread: three call sites need type="submit".
    <button
      type="button"
      {...rest}
      className={className}
      disabled={inert}
      // After the spread, and attached even with no caller handler: a submit
      // button's second click must not post the form again.
      onClick={guardClick(onClick)}
    >
      {content}
    </button>
  );
}

// Nothing the API accepts is idempotent, so a double click on a mutating action
// runs it twice. Exported for tests that need to wait the window out.
export const ACTION_CLICK_TIMEOUT = 500;

/**
 * A swallowed click is stopped dead rather than merely ignored, so it cannot
 * submit a form or reach a parent's handler either. That rules out lodash
 * throttle, which suppresses by not calling at all.
 */
function useClickGuard() {
  const justClicked = useRef(false);
  // No browser hands out 0 as a timer id and clearTimeout ignores it, so the
  // ref can stay a plain number.
  const timer = useRef(0);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (onClick: MouseEventHandler<HTMLButtonElement> | undefined) =>
    (event: MouseEvent<HTMLButtonElement>) => {
      if (justClicked.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      justClicked.current = true;
      timer.current = window.setTimeout(() => {
        justClicked.current = false;
      }, ACTION_CLICK_TIMEOUT);
      onClick?.(event);
    };
}

function cancelNavigation(event: MouseEvent) {
  event.preventDefault();
}

function buttonClassName(props: Props) {
  const { variant = "primary", size = "medium" } = props;
  return cls(styles.button, "talaera-modern-button", props.className, {
    [styles.primary]: variant === "primary",
    [styles.secondary]: variant === "secondary",
    [styles.plainButton]: variant === "plain",
    [styles.danger]: variant === "danger",
    [styles.dangerOutline]: variant === "danger-outline",
    [styles.iconButton]: !!props.Icon,
    [styles.disabled]: props.disabled,
    [styles.selected]: props.selected,
    [styles.loading]: props.loading,
    [styles.extraSmall]: size === "extra-small",
    [styles.small]: size === "small",
    [styles.medium]: size === "medium",
    [styles.large]: size === "large",
    [styles.extraLarge]: size === "extra-large",
    // A block-level flex container fills its row, which a <button> resists and
    // an <a> does not.
    [styles.inline]: props.as === "a" || props.as === "link",
  });
}
